/**
 * API paths shared with query keys. Keep in sync with the NestJS controllers.
 */
export const API_PATHS = {
  health: 'health',
  auth: {
    login: 'auth/login',
    verifyMfa: 'auth/mfa/verify',
    me: 'auth/me',
    setupMfa: 'auth/mfa/setup',
    enableMfa: 'auth/mfa/enable',
    resetMfa: 'auth/mfa/reset',
    changePassword: 'auth/change-password',
  },
  /** Site settings of the signed-in tenant (D4a — feature core-tenant/site-working-hours). */
  tenant: {
    settings: 'tenant/settings',
  },
  users: {
    list: 'users',
    create: 'users',
    byId: (id: number) => `users/${id}` as const,
  },
  trials: {
    list: 'trials',
    create: 'trials',
    byId: (id: number) => `trials/${id}` as const,
    visits: (id: number) => `trials/${id}/visits` as const,
    /** D.4 (mail 6): edit of one catalog visit. */
    visitById: (id: number, visitId: number) => `trials/${id}/visits/${visitId}` as const,
    /** #44 pkt 6: persistent catalog order — swaps one visit with its neighbour. */
    visitReorder: (id: number, visitId: number) => `trials/${id}/visits/${visitId}/reorder` as const,
    /** #44 pkt 6 (round 2): moves a contiguous block of selected visits as one unit. */
    visitsReorder: (id: number) => `trials/${id}/visits/reorder` as const,
    /** D2 (#40): „++" bulk creation of catalog visits in ONE transaction. */
    visitsBulk: (id: number) => `trials/${id}/visits/bulk` as const,
    /** Visit groups of one trial (#40): GET lists, POST creates. */
    visitGroups: (id: number) => `trials/${id}/visit-groups` as const,
    /** One group (#40): PATCH renames, DELETE removes (409 when visits are attached). */
    visitGroupById: (id: number, groupId: number) =>
      `trials/${id}/visit-groups/${groupId}` as const,
    /** Per-mode visit-window ranges (#36): list/create under one visit, patch one mode. */
    windowModes: (id: number, visitId: number) => `trials/${id}/visits/${visitId}/window-modes` as const,
    windowModeById: (id: number, visitId: number, modeId: number) =>
      `trials/${id}/visits/${visitId}/window-modes/${modeId}` as const,
    /** Participants of one trial (REQ-VIS-006) — served by the Domain 3 controller. */
    enrollments: (id: number) => `trials/${id}/enrollments` as const,
    roles: (id: number) => `trials/${id}/roles` as const,
    roleById: (id: number, roleId: number) => `trials/${id}/roles/${roleId}` as const,
    personnel: (id: number) => `trials/${id}/personnel` as const,
    personnelById: (id: number, assignmentId: number) => `trials/${id}/personnel/${assignmentId}` as const,
    /** Qualification criteria configuration of one trial (REQ-PAT-018…020) — served by the Domain 3 controller. */
    criteria: (id: number) => `trials/${id}/criteria` as const,
    criterionById: (id: number, criteriaId: number) => `trials/${id}/criteria/${criteriaId}` as const,
    criterionAnswers: (id: number, criteriaId: number) =>
      `trials/${id}/criteria/${criteriaId}/answers` as const,
    criterionAnswerById: (id: number, criteriaId: number, answerId: number) =>
      `trials/${id}/criteria/${criteriaId}/answers/${answerId}` as const,
  },
  personnel: {
    list: 'personnel',
    byId: (id: number) => `personnel/${id}` as const,
  },
  patients: {
    list: 'patients',
    create: 'patients',
    byId: (id: number) => `patients/${id}` as const,
    /** Participation history of one chart — POST creates, GET reads. */
    enrollments: (id: number) => `patients/${id}/enrollments` as const,
    /** Qualification dialog of one participation (issue #30): checklist GET, gate-4 cell PUT, R4 decision POST. */
    qualification: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/qualification` as const,
    qualificationAnswers: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/qualification/answers` as const,
    qualificationDecision: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/qualification/decision` as const,
    /** Participation decisions panel (issue #29): GET panel, POST records one decision. */
    decisions: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/decisions` as const,
    /**
     * Manual schedule changes of one participation (issue #29 B6/B7):
     * GET reads the change log, POST adds a catalog position; DELETE of one
     * scheduled visit goes through `scheduleVisit`. Implemented by the
     * backend V3 atom (migration 0023); DTO contracts live in
     * `@ctms/contracts` `patients/schedule-log.schema.ts`.
     */
    schedule: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/schedule` as const,
    scheduleVisit: (id: number, enrollmentId: number, visitId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/schedule/${visitId}` as const,
    /**
     * Bulk schedule changes (issue #39): POST assign moves catalog positions
     * into the schedule, POST unassign removes scheduled visits — both
     * ALL-OR-NOTHING with one `added`/`removed` log row per changed visit.
     */
    scheduleAssign: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/schedule/assign` as const,
    scheduleUnassign: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/schedule/unassign` as const,
    /**
     * Template visits (issue #41): GET reads the dialog state (the configured
     * preset set with per-enrollment `alreadyAdded` / `repeatable` flags),
     * POST adds the selected presets in ONE all-or-nothing bulk (409
     * TEMPLATE_VISIT_DUPLICATE only for the one-time OUT category).
     * Contracts: `@ctms/contracts` `patients/template-visits.schema.ts`.
     */
    scheduleTemplateVisits: (id: number, enrollmentId: number) =>
      `patients/${id}/enrollments/${enrollmentId}/schedule/template-visits` as const,
  },
  visits: {
    /** Day plan of one trial (REQ-VIS-002) — Domain 4 cluster `modules/visits`. */
    dayPlan: 'visits',
    /** Catalogue of additional procedures of a trial (REQ-VIS-040) — the source list of REQ-VIS-050. */
    procedureCatalogue: 'visits/procedure-catalogue',
    /** Working list of one visit: saved executions joined with the visit pattern (REQ-VIS-045). */
    procedureWorkList: 'visits/procedure-work-list',
    /** Persists the whole working list of one visit (REQ-VIS-045 step 6, REQ-VIS-049). */
    procedureDraft: (visitId: number) => `visits/${visitId}/procedure-draft` as const,
    /** Executions of one visit — POST adds outside the pattern (REQ-VIS-050). */
    procedureExecutions: 'visits/procedure-executions',
    /** One execution: PATCH ticks/unt ticks (REQ-VIS-044/049), DELETE drops an outside-pattern line (REQ-VIS-048). */
    procedureExecution: (id: number) => `visits/procedure-executions/${id}` as const,
    /**
     * Planned date of ONE patient visit (REQ-VIS-015) — PATCH books the day,
     * an explicit `null` clears the booking. Domain 4 endpoint, the write itself
     * goes through the Domain 3 service door.
     */
    schedule: (visitId: number) => `visits/${visitId}/schedule` as const,
    /** Open to-do tasks of one enrollment (REQ-VIS-008) — POST opens, GET lists (REQ-VIS-051). */
    patientTasks: 'visits/patient-tasks',
    /** Closes ONE task (REQ-VIS-051) — the closing day is the server's today. */
    patientTaskClose: (id: number) => `visits/patient-tasks/${id}/close` as const,
    /** Contacts with a patient (REQ-VIS-038) — POST records SMS/phone, GET lists one visit's contacts. */
    patientContacts: 'visits/patient-contacts',
    /**
     * Performed side of ONE patient visit (REQ-VIS-020/025/026/028) — PATCH
     * registers the day (+ optional hour, type, comment); `date: null` undoes.
     */
    registration: (visitId: number) => `visits/${visitId}/registration` as const,
    /** Kits registered at ONE visit (REQ-KIT-013..022) — GET reads, PUT replaces kits + flag. */
    kits: (visitId: number) => `visits/${visitId}/kits` as const,
    /** Personnel of ONE visit (REQ-VIS-027, #32) — GET reads, PUT replaces the full register. */
    personnel: (visitId: number) => `visits/${visitId}/personnel` as const,
  },
  dictionaries: {
    sponsors: 'dictionaries/sponsors',
    cros: 'dictionaries/cros',
  },
  kits: {
    laboratories: 'kits/laboratories',
    laboratory: (id: number) => `kits/laboratories/${id}` as const,
    list: 'kits',
    create: 'kits',
    byId: (id: number) => `kits/${id}` as const,
    expirations: (id: number) => `kits/${id}/expirations` as const,
    shipments: 'kits/shipments',
    shipment: (id: number) => `kits/shipments/${id}` as const,
    stock: 'kits/stock',
    /** Kit demand forecast and shortage warnings (REQ-KIT-027..029). */
    demandForecast: 'kits/demand-forecast',
    /** Virtual basket of kits awaiting destruction (REQ-KIT-030..032). */
    out: 'kits/out',
    outItem: (id: number) => `kits/out/${id}` as const,
    outProtocols: 'kits/out/protocols',
    outProtocol: (id: number) => `kits/out/protocols/${id}` as const,
    /** Signed stock corrections register (REQ-KIT-033). */
    corrections: 'kits/corrections',
  },
  /** Cockpit personalization (#7, REQ-VIS-005): favorite trial bars + last-trial recall. */
  cockpit: {
    trialBars: 'cockpit/trial-bars',
    reorderBars: 'cockpit/trial-bars/reorder',
    barById: (barId: number) => `cockpit/trial-bars/${barId}` as const,
    barItems: (barId: number) => `cockpit/trial-bars/${barId}/items` as const,
    barItemById: (barId: number, itemId: number) =>
      `cockpit/trial-bars/${barId}/items/${itemId}` as const,
    reorderBarItems: (barId: number) => `cockpit/trial-bars/${barId}/items/reorder` as const,
    state: 'cockpit/state',
  },
  documents: {
    /** RPC-style VCL composition of one patient visit (REQ-DOC-001…016) — JSON payload. */
    generateVcl: 'documents/vcl/generate',
    /** The same VCL rendered to A4 PDF by the worker (REQ-DOC-007/008) — `application/pdf` bytes. */
    renderVclPdf: 'documents/vcl/render-pdf',
    /** RPC-style composition of one destruction protocol (REQ-DOC-018) — JSON payload. */
    generateKitDestruction: 'documents/kit-destruction/generate',
    /** The same protocol rendered to A4 PDF by the worker (REQ-DOC-007/018) — `application/pdf` bytes. */
    renderKitDestructionPdf: 'documents/kit-destruction/render-pdf',
    /** RPC-style composition of one patient todo-list card (REQ-DOC-017) — JSON payload. */
    generatePatientTodo: 'documents/patient-todo/generate',
    /** The same card rendered to A4 PDF by the worker (REQ-DOC-007/017) — `application/pdf` bytes. */
    renderPatientTodoPdf: 'documents/patient-todo/render-pdf',
  },
} as const;
